import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IUserGroupsService } from "services/UserGroupsService";
import { SETTING_TYPES } from "services/userSettings/SettingTypes";
import { IUserSettingsService } from "services/UserSettingsService";
import { IUserTasksService } from "services/UserTasksService";
import { UserTaskFiltersModel, UserTaskModel } from "services/api/cplapi/CPLAPI";
import { UserGroupModel } from "services/api/cplauthapi/CPLAuthAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";

const initialState = {
  UserTasks: new Array<UserTaskModel>(),
  UserGroups: new Array<UserGroupModel>(),
  IncludeAllCompany: false,
  IncludeSubordinates: false,
  IncludeUserGroup: false,
  IncludeArchived: false,
  LoadingTasks: false,
  LoadingUserGroups: false,
  SavedColumnOrder: Array<string>(),
  DueIn: -1,
};

export const userTasksTableSlice = createSlice({
  name: "userTasks",
  initialState,
  reducers: {
    setIncludeAllCompany: (state, action) => {
      state.IncludeAllCompany = action.payload;
    },
    setIncludeSubordinates: (state, action) => {
      state.IncludeSubordinates = action.payload;
    },
    setIncludeUserGroup: (state, action) => {
      state.IncludeUserGroup = action.payload;
    },
    setIncludeArchived: (state, action) => {
      state.IncludeArchived = action.payload;
    },
    setDueIn: (state, action) => {
      state.DueIn = action.payload;
    },
    resetData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserTasks.pending, (state) => {
        state.LoadingTasks = true;
      })
      .addCase(getUserTasks.fulfilled, (state, action) => {
        state.UserTasks = action.payload;
        state.LoadingTasks = false;
      })
      .addCase(getUserTasks.rejected, (state) => {
        state.LoadingTasks = false;
      })
      .addCase(fetchUserTasksTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.payload;
      })
      .addCase(getUserGroups.pending, (state) => {
        state.LoadingUserGroups = true;
      })
      .addCase(getUserGroups.rejected, (state) => {
        state.LoadingUserGroups = false;
      })
      .addCase(getUserGroups.fulfilled, (state, action) => {
        state.LoadingUserGroups = false;
        state.UserGroups = action.payload;
      });
  },
});

export const { resetData } = userTasksTableSlice.actions;

export const getUserTasks = createAsyncThunk<UserTaskModel[], { userTaskFilters: UserTaskFiltersModel }>(
  "UserTasks/GetUserTasks",
  async ({ userTaskFilters }) => {
    const usersTaskService = container.get<IUserTasksService>(SERVICE_TYPES.UserTasksService);
    return await usersTaskService.getUsersTasks(userTaskFilters);
  }
);

export const fetchUserTasksTableColumnOrder = createAsyncThunk("UserTasks/FetchUserTasksTableColumnOrder", async () => {
  const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
  return await userSettingsService.getColumnOrder(SETTING_TYPES.UserTasksTableColumnKey);
});

export const updateUserTasksTableColumnOrder = createAsyncThunk(
  "UserTasks/UpdateUserTasksTableColumnOrder",
  async (tableColumnOrder: string[]) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setColumnOrder(tableColumnOrder, SETTING_TYPES.UserTasksTableColumnKey);
  }
);

export const getUserGroups = createAsyncThunk("UserTasks/GetAllUserGroups", async () => {
  const UserGroupsService = container.get<IUserGroupsService>(SERVICE_TYPES.UserGroupsService);
  return await UserGroupsService.getAllUserGroups();
});

export const { setIncludeAllCompany, setIncludeUserGroup, setIncludeSubordinates, setIncludeArchived, setDueIn } =
  userTasksTableSlice.actions;
export const selectAllUserTasks = (state: RootState) => state.userTasks.UserTasks;
export const selectUserGroups = (state: RootState) => state.userTasks.UserGroups;
export const selectLoadingTasks = (state: RootState) => state.userTasks.LoadingTasks;
export const selectLoadingUserGroups = (state: RootState) => state.userTasks.LoadingUserGroups;
export const selectSavedColumnOrder = (state: RootState) => state.userTasks.SavedColumnOrder;
export const selectIncludeAllCompany = (state: RootState) => state.userTasks.IncludeAllCompany;
export const selectIncludeSubordinates = (state: RootState) => state.userTasks.IncludeSubordinates;
export const selectIncludeUserGroup = (state: RootState) => state.userTasks.IncludeUserGroup;
export const selectIncludeArchived = (state: RootState) => state.userTasks.IncludeArchived;
export const selectDueIn = (state: RootState) => state.userTasks.DueIn;

export default userTasksTableSlice.reducer;
